<template>
  <div class="text-base md:text-lg">
    {{ displayString }}
    <div v-if="selection.subSelections && selection.subSelections.length">
      <div
        v-for="(subSelection, index) in selection.subSelections"
        :key="index"
        class="ml-2"
      >
        <RequisiteSimpleDataSelect
          :selection="subSelection"
          :related-requisites="relatedRequisites"
        />
      </div>
    </div>
    <ul v-else-if="selection.values && selection.values.length">
      <li
        v-for="(value, valueIndex) in getNonEmptyNormalizedValues(selection.values, selection.condition)"
        :key="valueIndex"
        class="m-0 p-0"
      >
        <span v-if="selection.condition === 'courses'">
          <span
            v-for="(courseValue, index) in value.value"
            :key="'course-value-'+ index"
          >
            <template v-if="cmCourseById(courseValue)">
              <button
                class="link cursor-pointer text-left"
                @click="$emit('showModal', generateModalEventBody('course', courseValue))"
              >
                {{ getCourseLinkLabel(courseValue) }}
              </button>
            </template>
            <template v-else-if="displayRequisiteIdInlineIfEntityNotFound || !enableFetchingRequisitesWithLatestRevisions">
              <span>{{ courseValue }}</span>
            </template>
            <br />
            <strong v-if="value.logic && index < value.value.length - 1">{{value.logic.toUpperCase()}}
            </strong>
          </span>
        </span>
        <span v-else-if="selection.condition === 'programs'">
          <span
            v-for="(programValue, index) in value.value"
            :key="'program-value-'+ index"
          >
            <template v-if="cmProgramById(programValue)">
              <button
                class="link cursor-pointer text-left"
                @click="$emit('showModal', generateModalEventBody('program', programValue))"
              >
                {{getProgramLinkLabel(programValue) }}
              </button>
            </template>
            <template v-else-if="displayRequisiteIdInlineIfEntityNotFound || !enableFetchingRequisitesWithLatestRevisions">
              <span>{{ programValue }}</span>
            </template>
            <br />
            <strong v-if="value.logic && index < value.value.length - 1">{{value.logic.toUpperCase()}}
            </strong>
          </span>
        </span>
        <span v-else-if="selection.condition === 'courseSets'">
          <span
            v-for="(courseSet,i) in value.value"
            :key="i"
          >
            <button
              class="link cursor-pointer text-left"
              @click="$emit('showModal', generateModalEventBody('courseSet', courseSet))"
            >
              {{ getCourseSetLabel(courseSet) }}
            </button>
            <template v-if="i !== value.value.length-1">
              <br />
              <strong>{{ (value.logic).toUpperCase() }}
              </strong>
            </template>
          </span>
        </span>
        <span v-else-if="selection.condition === 'requisiteSets'">
          <span
            v-for="(requisiteSet,i) in value.value"
            :key="i"
          >
            <button
              class="link cursor-pointer text-left"
              @click="$emit('showModal', generateModalEventBody('requisiteSet', requisiteSet))"
            >
              {{ getRequisiteSetLabel(requisiteSet) }}
            </button>
            <template v-if="i !== value.value.length-1">
              <br />
              <strong>{{ (value.logic).toUpperCase() }}
              </strong>
            </template>
          </span>
        </span>
        <span v-else>
          {{ value }}
        </span>
      </li>
    </ul>
    <div
      v-else
      class="text-center text-gray-500 bg-gray-200 rounded p-3 mb-2 border"
    >No selection provided</div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import _isObject from 'lodash/isObject'
import { decamelize } from '@/utils/generalUtils'
import {
  generateCourseLabel,
  extractCourseLabels,
  generateCourseCreditsRange,
  generateProgramLabel,
  extractProgramLabels,
} from '@/utils/dataLabels'
import useFeatureFlags from '@/composables/useFeatureFlags'
import useInterpolation from '@/composables/useInterpolation'

import {
  SHOW_REQUISITES_SUMMED_CREDITS,
  TITLE_INTERPOLATIONS,
} from '@/constants/featureFlags'
export default {
  name: 'RequisiteSimpleDataSelect',
  props: {
    selection: {
      type: Object,
      required: true,
    },
    relatedRequisites: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    const { getInterpolatedFieldValue } = useInterpolation()
    const {
      showRequisitesCredits,
      enableTitleInterpolations,
    } = useFeatureFlags({
      showRequisitesCredits: SHOW_REQUISITES_SUMMED_CREDITS,
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
    })

    return {
      showRequisitesCredits,
      getInterpolatedFieldValue,
      enableTitleInterpolations,
    }
  },
  data() {
    return {
      details: null,
    }
  },
  computed: {
    ...mapState('settings', ['school']),
    ...mapGetters('settings', [
      'effectiveDatesRangeAsString',
      'enableFetchingRequisitesWithLatestRevisions',
      'displayRequisiteIdInlineIfEntityNotFound',
    ]),
    displayString() {
      switch (this.selection.condition) {
        case 'anyOf':
          return 'Any of:'
        case 'allOf':
          return 'All of:'
        case 'numberOf':
          return `At least ${this.selection.number} of:`
        case 'freeformText':
          return `- ${this.selection.text}`
        case 'courses':
        case 'programs':
        case 'courseSets':
          return ''
        case 'requisiteSets':
          return ''
        default:
          return `These ${decamelize(this.selection.condition).toLowerCase()}:`
      }
    },
  },
  methods: {
    generateModalEventBody(type, id) {
      const body = { type }

      if (type === 'course') {
        const course = this.cmCourseById(id)
        body.details = {
          path: 'courses',
          course,
          label: generateCourseLabel(
            course,
            this.displayRequisiteIdInlineIfEntityNotFound ? id : null,
            {
              getInterpolatedFieldValue: this.enableTitleInterpolations
                ? this.getInterpolatedFieldValue
                : undefined,
            }
          ),
          description: course.description,
          id,
        }
      }
      if (type === 'program') {
        const program = this.cmProgramById(id)
        body.details = {
          path: 'programs',
          label: generateProgramLabel(program, this.school, {
            getInterpolatedFieldValue: this.enableTitleInterpolations
              ? this.getInterpolatedFieldValue
              : undefined,
          }),
          description: program.description,
          id,
        }
      }
      if (type === 'courseSet') {
        body.details = {
          courseSet: this.courseSetById(id),
          id,
        }
      }
      if (type === 'requisiteSet') {
        body.details = {
          requisiteSet: this.requisiteSetById(id),
          id,
        }
      }
      return body
    },
    getNormalizedValues(values) {
      return values.map(
        (v) => (_isObject(v) ? v : { value: [v], logic: 'and' })
      )
    },
    getNonEmptyNormalizedValues(values, conditionType) {
      if (
        !this.enableFetchingRequisitesWithLatestRevisions ||
        this.displayRequisiteIdInlineIfEntityNotFound
      ) {
        return this.getNormalizedValues(values)
      }
      const conditionTypeSolvers = {
        courses: this.cmCourseById,
        programs: this.cmProgramById,
        courseSets: this.courseSetById,
        requisiteSets: this.requisiteSetById,
      }
      const entityExistenceCheck = (id) => {
        const result = conditionTypeSolvers[conditionType](id) ? id : null
        return this.enableFetchingRequisitesWithLatestRevisions ? result : id
      }

      return values
        .map((value) => {
          if (_isObject(value)) {
            if (!Array.isArray(value.value)) {
              value.value = [value.value]
            }
            const result = value.value.map(entityExistenceCheck).filter(Boolean)
            if (result.length) return { value: result, logic: value.logic }
          } else {
            const valueArray = Array.isArray(value) ? value : [value]
            const result = valueArray.map(entityExistenceCheck).filter(Boolean)
            if (result.length) return { value: result, logic: 'and' }
          }
        })
        .filter(Boolean)
    },
    cmCourseById(id) {
      return (
        this.relatedRequisites.courses?.[id] ||
        this.relatedRequisites.courses?.[`deprecated_${id}`]
      )
    },
    cmProgramById(id) {
      return (
        this.relatedRequisites.programs?.[id] ||
        this.relatedRequisites.programs?.[`deprecated_${id}`]
      )
    },
    courseSetById(id) {
      return this.relatedRequisites.courseSets?.[id]
    },
    requisiteSetById(id) {
      return this.relatedRequisites.requisiteSets?.[id]
    },
    getCourseLinkLabel(courseId) {
      const course = this.cmCourseById(courseId)
      if (!course) return courseId
      // NOTE: we do not need to use the displayRequisiteIdInlineIfEntityNotFound
      // flag here because this function already returns the ID if the
      // course is not found
      if (this.showRequisitesCredits) {
        return `${generateCourseLabel(course, undefined, {
          getInterpolatedFieldValue: this.enableTitleInterpolations
            ? this.getInterpolatedFieldValue
            : undefined,
        })} ${generateCourseCreditsRange(course, this.$localize) || ''}`
      }
      return generateCourseLabel(course, undefined, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
      })
    },
    getProgramLinkLabel(programId) {
      const program = this.cmProgramById(programId)
      if (!program) return programId
      return generateProgramLabel(program, this.school, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
      })
    },
    getCourseSetLabel(courseSetId) {
      return this.courseSetById(courseSetId)
        ? this.courseSetById(courseSetId).name
        : 'Untitled Course Set'
    },
    getRequisiteSetLabel(requisiteSetId) {
      return this.requisiteSetById(requisiteSetId)
        ? this.requisiteSetById(requisiteSetId).name
        : 'Untitled Requirement Set'
    },
    courseLabels(courseId) {
      const course = this.cmCourseById(courseId)
      if (!course) return { name: courseId }
      return extractCourseLabels(course)
    },
    programLabels(programId) {
      const program = this.cmProgramById(programId)
      if (!program) return { name: programId }
      const { code, name } = extractProgramLabels(program)
      return this.school === 'scsu' ? { name } : { code, name }
    },
  },
}
</script>

<style scoped lang="scss">
ul {
  list-style-type: disc;
  margin-left: 1rem;
  padding: 0;

  li {
    text-align: left;

    & > * {
      vertical-align: initial;
    }

    button {
      vertical-align: top;
    }
  }
}
.link {
  text-decoration: underline;
  color: var(--theme-light, #006cb8);
}
</style>
